import React from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  AppWindow, Users, Zap, AlertCircle, Sparkles,
  Building2, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { SectionCard, Button, Badge } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { DateRangeTool, presetToRange } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import { StatStrip } from "../components/StatStrip";
import { HowItWorks } from "../components/HowItWorks";
import { SERVER_URL } from "../../../utils/serverConfig";

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const intensityTone = (v) => (v === "high" ? "brand" : v === "medium" ? "info" : "neutral");
const intensityLabel = (v) => (v === "high" ? "Heavy use" : v === "medium" ? "Moderate" : v === "low" ? "Light" : v);
const helpTone = (v) => (v === "high" ? "positive" : v === "mixed" ? "warning" : v === "low" ? "negative" : "neutral");

// Rotating status lines while analyse runs — human, not "embeddings".
const ANALYSE_STATUS_LINES = [
  "Watching how the team actually uses this tool…",
  "Tracing sessions, titles, and quiet patterns…",
  "Connecting the dots across people’s workdays…",
  "Reading the story behind the clicks…",
  "Sorting heavy users from drive-bys…",
  "Looking for signal in the busy hours…",
  "Piecing together what this tool is really for…",
  "Almost there — turning usage into a clear take…",
];

export default function AppUsageV2() {
  const t = useDashTheme();
  const { user } = useAuth();

  const [member, setMember] = React.useState(null);
  const [range, setRange] = React.useState(() => presetToRange("7d"));
  const [target, setTarget] = React.useState(null);

  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [catalog, setCatalog] = React.useState([]);
  const [loadingCatalog, setLoadingCatalog] = React.useState(false);

  const [analysing, setAnalysing] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [statusLine, setStatusLine] = React.useState(ANALYSE_STATUS_LINES[0]);

  React.useEffect(() => {
    if (!analysing) return undefined;
    setStatusLine(ANALYSE_STATUS_LINES[0]);
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % ANALYSE_STATUS_LINES.length;
      setStatusLine(ANALYSE_STATUS_LINES[i]);
    }, 2400);
    return () => clearInterval(id);
  }, [analysing]);

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json?.success && json.data?.accessibleUsers) {
          setAccessibleUsers(json.data.accessibleUsers);
        }
      } catch (_) { /* non-blocking */ } finally {
        setLoadingUsers(false);
      }
    })();
  }, [user]);

  const members = React.useMemo(() => [
    { value: null, label: "Whole team" },
    ...accessibleUsers.map((u) => ({
      value: u._id || u.id,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
      sub: u.position || u.email,
    })),
  ], [accessibleUsers]);

  // Reload catalog when period / member changes; clear prior analysis
  React.useEffect(() => {
    setResult(null);
    setError(null);
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({
          startDate: fmt(range.start),
          endDate: fmt(range.end),
        });
        if (member) params.set("userId", member);
        const res = await fetch(`${SERVER_URL}/api/v1/app-usage/catalog?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (!json?.success) throw new Error(json?.message || "Catalog failed");
        const opts = (json.data?.options || []).map((o) => ({
          value: o.value,
          label: o.kind === "site" ? `${o.label} (site)` : o.label,
        }));
        setCatalog(opts);
        if (target && !opts.some((o) => o.value === target)) setTarget(null);
      } catch (e) {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, member]);

  const analyse = async () => {
    if (!target) {
      toast.error("Select an application or website first");
      return;
    }
    setAnalysing(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/app-usage/analyse`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          target,
          startDate: fmt(range.start),
          endDate: fmt(range.end),
          userId: member || undefined,
        }),
      });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.message || "Analysis failed");
      setResult(json.data);
    } catch (e) {
      setError(e.message || "Analysis failed");
      toast.error(e.message || "Analysis failed");
    } finally {
      setAnalysing(false);
    }
  };

  const report = result?.report;
  const dateSubtitle = `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return (
    <PageShell
      eyebrow="Insights · Tool utilisation"
      title="App usage"
      subtitle="Pick a tool your team is trying — see who uses it, what they do, and whether it helps"
      icon={<AppWindow size={22} />}
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<AppWindow size={13} />}
            value={target}
            options={catalog}
            onChange={(v) => { setTarget(v); setResult(null); setError(null); }}
            placeholder={loadingCatalog ? "Loading tools…" : "Select app or website"}
            searchThreshold={5}
            panelWidth={280}
            placement="bottomRight"
            allowCustom
          />
          <span className="tg-tool-sep" />
          <DateRangeTool
            range={range}
            onChange={setRange}
            layoutId="appUsageRangeChip"
            presets={[
              { key: "yesterday", label: "Yesterday" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "month", label: "This month" },
            ]}
          />
          <span className="tg-tool-sep" />
          <ToolSelect
            icon={<Users size={13} />}
            value={member}
            options={members}
            onChange={(v) => setMember(v || null)}
            placeholder={loadingUsers ? "Loading…" : "Whole team"}
            panelWidth={220}
            placement="bottomRight"
          />
        </div>
      }
      actions={
        <Button
          variant="primary"
          leftIcon={analysing ? <Sparkles size={15} /> : <Zap size={15} />}
          onClick={analyse}
          disabled={!target || analysing}
        >
          {analysing ? "Analysing…" : "Analyse"}
        </Button>
      }
    >
      {!target && !result && (
        <HowItWorks
          icon={<AppWindow size={18} />}
          steps={[
            "Select the application or website you want to evaluate",
            "Choose a period (analysis stays inside that window)",
            "Optionally narrow to one person — admins see the full team by default",
            "Click Analyse for an AI utilisation report",
          ]}
          bullets={[
            "Who uses the tool and for how long",
            "What they typically do inside it",
            "Depth of usage per person",
            "Whether the tool is helping — or should be reconsidered",
          ]}
          tip="Built for trials: pick GrowIt, TeamGrid, Chrome, etc. and see real team depth — not a wall of every app."
        />
      )}

      {target && !result && !analysing && !error && (
        <EmptyState
          icon={<Zap size={22} />}
          title={`Ready to analyse ${target}`}
          description={`${dateSubtitle}${member ? " · one member" : " · whole accessible team"}. We read measured usage and what people actually did in that window.`}
          action={<Button variant="primary" leftIcon={<Zap size={15} />} onClick={analyse}>Analyse</Button>}
        />
      )}

      {analysing && (
        <SectionCard title="Running analysis" subtitle={target} icon={<Sparkles size={16} />}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: `${t.s[8]}px ${t.s[4]}px`, gap: t.s[3] }}>
            <motion.span
              animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 1.3, repeat: Infinity }}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 52, height: 52, borderRadius: 16, background: t.c.primaryGradient, color: "#fff",
              }}
            >
              <Sparkles size={24} />
            </motion.span>
            <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text, textAlign: "center", minHeight: 28 }}>{statusLine}</div>
            <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Scoped to your selected period — not a full history scan</div>
          </div>
        </SectionCard>
      )}

      {error && !analysing && (
        <EmptyState icon={<AlertCircle size={22} />} title="Couldn't analyse" description={error}
          action={<Button variant="primary" onClick={analyse}>Retry</Button>} />
      )}

      {report && !analysing && (
        <>
          <StatStrip
            items={[
              { label: "People using it", value: report.userCount ?? result.usage?.userCount ?? 0, accent: "#5B5BD6" },
              { label: "Total time", value: report.totalTimeLabel || "0m", accent: "#0EA5E9" },
              {
                label: "Helpfulness",
                value: report.helpfulness?.label || "—",
                accent: report.helpfulness?.label === "high" ? "#16A34A" : report.helpfulness?.label === "low" ? "#E5484D" : "#F59E0B",
              },
              {
                label: "Score",
                value: report.helpfulness?.score != null ? `${report.helpfulness.score}/10` : "—",
                accent: "#64748B",
              },
            ]}
          />

          <SectionCard
            title={report.headline || target}
            subtitle={dateSubtitle}
            icon={<Building2 size={16} />}
            actions={<Badge tone="brand">{target}</Badge>}
          >
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: t.c.textSecondary }}>{report.summary}</p>
            {report.helpfulness?.rationale && (
              <div style={{
                marginTop: 14, padding: "12px 14px", borderRadius: 12,
                background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`,
                fontSize: 13.5, color: t.c.text, lineHeight: 1.55,
              }}>
                <span style={{ fontWeight: 700, marginRight: 6 }}>Helpfulness</span>
                <Badge tone={helpTone(report.helpfulness.label)}>{report.helpfulness.label}</Badge>
                <div style={{ marginTop: 8 }}>{report.helpfulness.rationale}</div>
              </div>
            )}
          </SectionCard>

          {(report.whyUsing || []).length > 0 && (
            <SectionCard title="Why the team uses it" subtitle="Inferred purpose from activity" icon={<CheckCircle2 size={16} />} style={{ marginTop: t.s[5] }}>
              <ul style={{ margin: 0, paddingLeft: 18, color: t.c.textSecondary, fontSize: 13.5, lineHeight: 1.6 }}>
                {report.whyUsing.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
              </ul>
            </SectionCard>
          )}

          {(report.insights || []).length > 0 && (
            <SectionCard title="Key insights" icon={<Sparkles size={16} />} style={{ marginTop: t.s[5] }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {report.insights.map((item, i) => (
                  <div key={i} style={{
                    padding: "10px 12px", borderRadius: 10, border: `1px solid ${t.c.border}`,
                    background: t.c.surfaceMuted, fontSize: 13.5, color: t.c.text, lineHeight: 1.55,
                  }}>{item}</div>
                ))}
              </div>
            </SectionCard>
          )}

          {(report.users || []).length > 0 && (
            <SectionCard
              title="Who uses it"
              subtitle="Time spent, what they do, and why"
              icon={<Users size={16} />}
              style={{ marginTop: t.s[5] }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {report.users.filter((u) => u?.name && !/weight\s*:/i.test(u.name)).map((u, i) => (
                  <div
                    key={`${u.name}-${i}`}
                    style={{
                      padding: "14px 16px", borderRadius: 12,
                      border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14.5, fontWeight: 700, color: t.c.text }}>{u.name}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                          {u.timeLabel || (u.minutes ? `${u.minutes}m` : "—")}
                        </span>
                        {u.intensity && <Badge tone={intensityTone(u.intensity)}>{intensityLabel(u.intensity)}</Badge>}
                      </span>
                    </div>
                    {u.whatTheyDo && !/weight\s*:/i.test(u.whatTheyDo) && (
                      <div style={{ marginTop: 8, fontSize: 13.5, color: t.c.text, lineHeight: 1.55 }}>
                        <span style={{ color: t.c.textTertiary, fontWeight: 600, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", marginRight: 8 }}>What</span>
                        {u.whatTheyDo}
                      </div>
                    )}
                    {u.why && (
                      <div style={{ marginTop: 6, fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.55 }}>
                        <span style={{ color: t.c.textTertiary, fontWeight: 600, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", marginRight: 8 }}>Why</span>
                        {u.why}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {(report.typicalWork || []).length > 0 && (
            <SectionCard title="Surfaces inside the tool" subtitle="Cleaned activity titles" icon={<AppWindow size={16} />} style={{ marginTop: t.s[5] }}>
              <ul style={{ margin: 0, paddingLeft: 18, color: t.c.textSecondary, fontSize: 13.5, lineHeight: 1.55 }}>
                {report.typicalWork.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
              </ul>
            </SectionCard>
          )}

          {report.recommendation && (
            <SectionCard title="Recommendation" icon={<Sparkles size={16} />} style={{ marginTop: t.s[5] }}>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: t.c.text }}>{report.recommendation}</p>
            </SectionCard>
          )}

          {(report.risks || []).length > 0 && (
            <SectionCard title="Watch-outs" icon={<AlertTriangle size={16} />} style={{ marginTop: t.s[5] }}>
              {(report.risks || []).map((r, i) => (
                <div key={i} style={{
                  fontSize: 13.5, color: t.c.text, padding: "10px 12px", borderRadius: 10,
                  background: "rgba(229,72,77,0.08)", marginBottom: 8,
                }}>{r}</div>
              ))}
            </SectionCard>
          )}

          <div style={{ marginTop: t.s[4], fontSize: 11.5, color: t.c.textTertiary }}>
            Analysis scoped to selected period
            {result.usage?.userCount != null ? ` · ${result.usage.userCount} people with measured usage` : ""}
          </div>
        </>
      )}
    </PageShell>
  );
}
